import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { WishlistProvider } from "./context/WishlistContext";
import Layout from "./components/Layout";

import Home from "./pages/Home";
import Cars from "./pages/Cars";
import CarDetail from "./pages/CarDetail";
import Products from "./pages/Products";
import ToolsLanding from "./pages/ToolsLanding";
import ToolsCategory from "./pages/ToolsCategory";
import WindowFilm from "./pages/WindowFilm";
import WindowFilmCategory from "./pages/WindowFilmCategory";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";

// Loaded on demand so shoppers don't download the admin console (and its chart library).
const Admin = lazy(() => import("./pages/Admin"));
const Account = lazy(() => import("./pages/Account"));
const QuoteReview = lazy(() => import("./pages/QuoteReview"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));

const PageLoading = () => <div role="status" className="pt-40 text-center text-[#6B645C]">Loading…</div>;

function PrivateRoute({ children, adminOnly }) {
    const { user, loading, isAdmin } = useAuth();
    if (loading) return <PageLoading />;
    if (!user) return <Navigate to="/login" replace />;
    if (adminOnly && !isAdmin) return <Navigate to="/" replace />;
    return children;
}

function NotFound() {
    return (
        <div data-testid="not-found" className="max-w-xl mx-auto px-6 pt-32 pb-24 text-center">
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">404</div>
            <h1 className="font-display text-4xl mb-4">This page has moved on.</h1>
            <p className="text-[#6B645C] mb-8">The page you're looking for isn't here. Try the showroom or our window film collection.</p>
            <div className="flex flex-wrap justify-center gap-3">
                <Link to="/cars" className="px-6 py-3 bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">Browse Vehicles</Link>
                <Link to="/automotive-window-film" className="px-6 py-3 border border-[#8C5A3C]/40 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold">Window Film</Link>
            </div>
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <CartProvider>
                <WishlistProvider>
                    <BrowserRouter>
                        <Suspense fallback={<PageLoading />}>
                            <Routes>
                                {/* Bare routes without main Layout */}
                                <Route path="/checkout/success" element={<CheckoutSuccess />} />
                                <Route path="/quote/:token" element={<QuoteReview />} />
                                <Route path="/admin/*" element={<PrivateRoute adminOnly><Admin /></PrivateRoute>} />

                                {/* Layout routes */}
                                <Route path="/" element={<Layout><Home /></Layout>} />
                                <Route path="/cars" element={<Layout><Cars /></Layout>} />
                                <Route path="/cars/:id" element={<Layout><CarDetail /></Layout>} />
                                <Route path="/tint" element={<Layout><Products category="tint" /></Layout>} />
                                <Route path="/automotive-window-film" element={<Layout><WindowFilm /></Layout>} />
                                <Route path="/automotive-window-film/:slug" element={<Layout><WindowFilmCategory /></Layout>} />
                                <Route path="/tools" element={<Layout><ToolsLanding /></Layout>} />
                                <Route path="/tools/:slug" element={<Layout><ToolsCategory /></Layout>} />
                                <Route path="/cart" element={<Layout><Cart /></Layout>} />
                                <Route path="/checkout" element={<Layout><Checkout /></Layout>} />
                                <Route path="/about" element={<Layout><About /></Layout>} />
                                <Route path="/contact" element={<Layout><Contact /></Layout>} />
                                <Route path="/login" element={<Layout><Login /></Layout>} />
                                <Route path="/register" element={<Layout><Register /></Layout>} />
                                <Route path="/reset" element={<Layout><ResetPassword /></Layout>} />
                                <Route path="/account" element={<PrivateRoute><Layout><Account /></Layout></PrivateRoute>} />
                                <Route path="*" element={<Layout><NotFound /></Layout>} />
                            </Routes>
                        </Suspense>
                    </BrowserRouter>
                    <Toaster position="top-right" theme="light" />
                </WishlistProvider>
            </CartProvider>
        </AuthProvider>
    );
}
